/**
 * RssFeedsDialog — Manage auto-ingestion RSS/Atom feeds for a company.
 * CRUD on /api/rss-feeds. Scheduler runs these hourly by default.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Rss, Plus, Trash2, Power, PowerOff, Eye, Brain } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RssFeedsDialog({ companyId, companyName, open, onOpenChange }) {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [interval, setIntervalHours] = useState("1");
  const [maxItems, setMaxItems] = useState("3");
  const [adding, setAdding] = useState(false);
  const [relevanceEnabled, setRelevanceEnabled] = useState(true);
  const [minScore, setMinScore] = useState(60);
  const [previewFor, setPreviewFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rss-feeds?company_id=${companyId}`, { withCredentials: true });
      setFeeds(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, companyId]);

  const add = async () => {
    if (!url.trim()) { toast.info("URL erforderlich"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/rss-feeds`, {
        feed_url: url.trim(),
        company_id: companyId,
        interval_hours: parseInt(interval),
        max_items_per_run: parseInt(maxItems),
        relevance_filter_enabled: relevanceEnabled,
        min_relevance_score: minScore,
      }, { withCredentials: true });
      setUrl("");
      toast.success("Feed hinzugefuegt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
    setAdding(false);
  };

  const openPreview = async (feed) => {
    setPreviewFor(feed);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/rss-feeds/${feed.id}/preview`, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview fehlgeschlagen");
      setPreviewFor(null);
    }
    setPreviewLoading(false);
  };

  const updateFilter = async (feed, patch) => {
    try {
      await axios.put(`${API}/rss-feeds/${feed.id}`, patch, { withCredentials: true });
      load();
    } catch { toast.error("Fehler"); }
  };

  const toggle = async (feed) => {
    try {
      await axios.put(`${API}/rss-feeds/${feed.id}`, { enabled: !feed.enabled }, { withCredentials: true });
      load();
    } catch { toast.error("Fehler"); }
  };

  const remove = async (feed) => {
    if (!window.confirm(`Feed "${feed.feed_url}" loeschen?`)) return;
    try {
      await axios.delete(`${API}/rss-feeds/${feed.id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rss-feeds-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Rss className="w-5 h-5 text-[#2563EB]" /> RSS-Auto-Import
            <span className="text-xs font-normal text-[var(--studio-text-muted)]">— {companyName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="bg-[var(--studio-bg)] rounded-lg p-3 space-y-3">
          <p className="text-xs text-[var(--studio-text-muted)]">
            Scheduler holt die Feeds automatisch in deinem Intervall und erstellt Draft-Content.
            Duplikate werden via Source-URL verhindert.
          </p>
          <div className="grid grid-cols-12 gap-2">
            <Input
              className="col-span-6"
              placeholder="https://example.com/feed.xml"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="rss-feed-url-input"
            />
            <Select value={interval} onValueChange={setIntervalHours}>
              <SelectTrigger className="col-span-3" data-testid="rss-feed-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 3, 6, 12, 24].map((n) => <SelectItem key={n} value={String(n)}>{n}h</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={maxItems} onValueChange={setMaxItems}>
              <SelectTrigger className="col-span-3" data-testid="rss-feed-max"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 5, 10].map((n) => <SelectItem key={n} value={String(n)}>{n} Eintraege</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={add}
            disabled={adding}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white w-full"
            data-testid="rss-feed-add-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Feed hinzufuegen
          </Button>

          {/* Relevance filter */}
          <div className="border-t border-[var(--studio-border)] pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={relevanceEnabled}
                onChange={(e) => setRelevanceEnabled(e.target.checked)}
                data-testid="rss-relevance-toggle"
                className="w-4 h-4 accent-[#2563EB]"
              />
              <Brain className="w-4 h-4 text-[#2563EB]" />
              KI-Relevanzfilter aktiv
              <span className="text-xs text-[var(--studio-text-muted)]">(nutzt Brand-Voice + Zielgruppe)</span>
            </label>
            {relevanceEnabled && (
              <div>
                <p className="text-xs text-[var(--studio-text-muted)] mb-1">
                  Mindest-Score (0-100): <b data-testid="rss-min-score-label">{minScore}</b> &middot; nur Artikel ab diesem Score werden importiert
                </p>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={minScore}
                  onChange={(e) => setMinScore(parseInt(e.target.value))}
                  data-testid="rss-min-score-slider"
                  className="w-full accent-[#2563EB]"
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2" data-testid="rss-feeds-list">
          <p className="text-sm font-medium">Aktive Feeds ({feeds.length})</p>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : feeds.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]">Keine Feeds. Fuege oben einen hinzu.</p>
          ) : feeds.map((f) => (
            <div key={f.id} className="flex items-start gap-2 p-3 border border-[var(--studio-border)] rounded-lg" data-testid={`rss-feed-${f.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.feed_url}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px]">
                  <Badge variant="secondary">{f.interval_hours}h</Badge>
                  <Badge variant="secondary">max {f.max_items_per_run}</Badge>
                  <span className={`px-1.5 py-0.5 rounded ${f.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                    {f.enabled ? "Aktiv" : "Pausiert"}
                  </span>
                  {f.relevance_filter_enabled && (
                    <span className="px-1.5 py-0.5 rounded bg-[#2563EB]/10 text-[#2563EB] flex items-center gap-0.5">
                      <Brain className="w-2.5 h-2.5" />
                      KI-Filter ≥ {f.min_relevance_score}
                    </span>
                  )}
                  {f.avg_relevance_score !== null && f.avg_relevance_score !== undefined && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                      Ø {f.avg_relevance_score}
                    </span>
                  )}
                  {(f.last_skipped_count > 0) && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Gefiltert (nicht importiert)">
                      {f.last_skipped_count} gefiltert
                    </span>
                  )}
                  {f.last_run_at && (
                    <span className="text-[var(--studio-text-muted)]">
                      · {new Date(f.last_run_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} ({f.last_imported || 0} importiert)
                    </span>
                  )}
                  {f.last_error && (
                    <span className="text-red-500 truncate max-w-[200px]" title={f.last_error}>· Fehler: {f.last_error}</span>
                  )}
                </div>
                {/* Per-feed threshold slider */}
                {f.relevance_filter_enabled && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] text-[var(--studio-text-muted)] min-w-[40px]">Score</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      defaultValue={f.min_relevance_score}
                      onMouseUp={(e) => updateFilter(f, { min_relevance_score: parseInt(e.currentTarget.value) })}
                      onTouchEnd={(e) => updateFilter(f, { min_relevance_score: parseInt(e.currentTarget.value) })}
                      data-testid={`feed-score-slider-${f.id}`}
                      className="flex-1 accent-[#2563EB]"
                    />
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => openPreview(f)} data-testid={`preview-feed-${f.id}`} title="Preview (KI-Scoring ohne Import)">
                <Eye className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => updateFilter(f, { relevance_filter_enabled: !f.relevance_filter_enabled })} data-testid={`toggle-filter-${f.id}`} title={f.relevance_filter_enabled ? "KI-Filter deaktivieren" : "KI-Filter aktivieren"}>
                <Brain className={`w-4 h-4 ${f.relevance_filter_enabled ? "text-[#2563EB]" : "text-gray-400"}`} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => toggle(f)} data-testid={`toggle-feed-${f.id}`}>
                {f.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4 text-green-600" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(f)} className="text-red-500" data-testid={`delete-feed-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Preview Dialog (nested) */}
        <Dialog open={!!previewFor} onOpenChange={(v) => { if (!v) { setPreviewFor(null); setPreview(null); } }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rss-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#2563EB]" /> RSS-Preview (KI-Scoring)
              </DialogTitle>
            </DialogHeader>
            {previewLoading ? (
              <div className="py-10 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2563EB]" />
                <p className="text-xs text-[var(--studio-text-muted)] mt-2">KI bewertet Artikel…</p>
              </div>
            ) : preview ? (
              <div className="space-y-3">
                <div className="text-xs text-[var(--studio-text-muted)] flex items-center gap-2 flex-wrap">
                  <span>{preview.feed_title}</span>
                  <Badge variant="secondary">{preview.total_entries} Artikel</Badge>
                  <Badge variant="secondary">Threshold: {preview.threshold}</Badge>
                </div>
                {preview.items.map((item, i) => {
                  const scoreColor = item.score >= 80 ? "bg-green-100 text-green-800"
                    : item.score >= 60 ? "bg-amber-100 text-amber-800"
                    : item.score >= 40 ? "bg-orange-100 text-orange-800"
                    : "bg-red-100 text-red-800";
                  return (
                    <div key={i} className="border border-[var(--studio-border)] rounded-lg p-3 space-y-1.5" data-testid={`preview-item-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium flex-1">{item.title}</p>
                        <Badge className={`${scoreColor} border-0`}>{item.score}</Badge>
                        {item.would_import ? (
                          <Badge className="bg-green-600 text-white border-0">Import</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-gray-200">Skip</Badge>
                        )}
                      </div>
                      <p className="text-xs text-[var(--studio-text-muted)] italic">"{item.reasoning}"</p>
                      {item.suggested_angle && (
                        <p className="text-xs"><b>Angle:</b> {item.suggested_angle}</p>
                      )}
                      <p className="text-[10px] text-[var(--studio-text-muted)]">Empfohl. Goal: {item.suggested_goal}</p>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
